import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { DataTable, EmptyState, Notice } from '@routes-plan/ui';
import StandardModal from '../../../components/modales/StandardModal';
import { formatDateTime } from '../devices/DeviceList';
import { describeDailyLimit } from './AlertCenter';
import { useT, type TextKey } from '../../../i18n';
import type { AlertItem } from './AlertCenter';

/**
 * Detalle de una alerta antifraude: cada abono o préstamo del representante que sumó a la alerta, con
 * la hora, el cobro, lo que se detectó y la instalación de la app que lo envió.
 */
interface Signal {
  id: number;
  kind: string;
  severity: 'warning' | 'high';
  entity: { type: 'pay' | 'lend' | 'expense'; id: number };
  route: { id: number; name: string | null } | null;
  detail: { minutes?: number; meters?: number; location?: string | null; limit?: string; max?: number; used?: number; adding?: number } | null;
  installationId: string | null;
  createdAt: string;
}

const AlertSignalsModal: React.FC<{ alert: AlertItem | null; onClose: () => void }> = ({ alert, onClose }) => {
  const t = useT();
  const [rows, setRows] = useState<Signal[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!alert) return;
    setRows(null);
    setFailed(false);
    axios
      .get<{ data: Signal[] }>(`alerts/${alert.id}/signals`)
      .then(({ data }) => setRows(data.data))
      .catch(() => setFailed(true));
  }, [alert]);

  const detailOf = (s: Signal) => {
    if (s.kind === 'clock_skew' && typeof s.detail?.minutes === 'number') {
      return t(s.detail.minutes < 0 ? 'alerts.signal.clockBehind' : 'alerts.signal.clockAhead', { min: Math.abs(s.detail.minutes) });
    }
    if (s.kind === 'far_payment' && typeof s.detail?.meters === 'number') return t('alerts.signal.farPayment', { m: s.detail.meters });
    if (s.kind === 'daily_limit') return describeDailyLimit(s.detail, t);
    return t(`alerts.kind.${s.kind}` as TextKey);
  };

  const operationOf = (s: Signal) =>
    t(`alerts.signal.op.${s.entity.type === 'pay' ? 'payment' : s.entity.type === 'expense' ? 'expense' : 'loan'}` as TextKey, { id: s.entity.id });

  return (
    <StandardModal
      open={Boolean(alert)}
      onClose={onClose}
      title={alert ? t(`alerts.kind.${alert.kind}` as TextKey) : ''}
      subtitle={alert?.salesRep ? alert.salesRep.fullName || alert.salesRep.username || undefined : undefined}
      maxWidth={960}
      actions={
        <>
          <span />
          <Button variant="contained" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      {alert && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t(`alerts.signal.help.${alert.kind}` as TextKey)}
        </Typography>
      )}
      {failed ? (
        <Notice severity="error">{t('alerts.loadError')}</Notice>
      ) : (
        <DataTable<Signal>
          rows={rows ?? []}
          loading={!rows}
          getRowId={(s) => s.id}
          ariaLabel={alert ? t(`alerts.kind.${alert.kind}` as TextKey) : ''}
          maxHeight={380}
          initialSort={{ columnId: 'when', direction: 'desc' }}
          columns={[
            { id: 'when', header: t('alerts.signal.colWhen'), value: (s) => s.createdAt, render: (s) => formatDateTime(s.createdAt), sortable: true },
            { id: 'operation', header: t('alerts.signal.colOperation'), value: (s) => operationOf(s) },
            { id: 'detail', header: t('alerts.signal.colDetail'), value: (s) => detailOf(s), wrap: true, width: 220 },
            { id: 'route', header: t('alerts.colRoute'), value: (s) => s.route?.name ?? '—', hideBelow: 'sm' },
            { id: 'installation', header: t('alerts.signal.colInstallation'), value: (s) => s.installationId ?? '—', hideBelow: 'md' },
          ]}
          emptyState={<EmptyState dense title={t('alerts.empty')} />}
        />
      )}
    </StandardModal>
  );
};

export default AlertSignalsModal;
